import React from 'react';
import defaultTheme from '@mui/material/styles/defaultTheme.js';
import {
  Check as CheckedIcon,
  Remove as IndeterminateIcon,
} from '@mui/icons-material';
import {alpha} from '@mui/material/styles/index.js';
import {darken, lighten} from '@mui/system';

import {DARK, LIGHT} from './Palettes.js';
import {THEME_VARIANTS} from './Constants.js';
import {round} from './utils.js';

const {pxToRem} = defaultTheme.typography;

// TODO: Magic numbers like 535, 10 etc should be constants and exported?

export default variant => {
  const darkmode = variant === THEME_VARIANTS.DARK;
  const palette = darkmode ? DARK : LIGHT;
  const borderColor = palette.divider;

  // Component Overrides object (Sorted Alphabetically)
  // Used as the value of `components` property in `createTheme` config object.
  return {
    MuiCssBaseline: {
      styleOverrides: theme => ({
        'html, body': {
          '&, &::backdrop': {
            backgroundColor: theme.palette.background.paper,
            fontFeatureSettings: `'kern' 1, 'ss01' 1`, // alternative numbers
            fontVariantNumeric: 'tabular-nums', // monospaced numbers
          },
        },
        'input[type="number"]': {
          '&::-webkit-inner-spin-button': {
            background: `transparent url(${
              darkmode
                ? 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAcAAAAKAQMAAABCAtZOAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAyNpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDYuMC1jMDAyIDc5LjE2NDQ2MCwgMjAyMC8wNS8xMi0xNjowNDoxNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RSZWY9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZVJlZiMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIDIwMjAgTWFjaW50b3NoIiB4bXBNTTpJbnN0YW5jZUlEPSJ4bXAuaWlkOjNFNjE3MzRGRDBENTExRUE4OEFBRTEzMUQxMTI3QzU2IiB4bXBNTTpEb2N1bWVudElEPSJ4bXAuZGlkOjNFNjE3MzUwRDBENTExRUE4OEFBRTEzMUQxMTI3QzU2Ij4gPHhtcE1NOkRlcml2ZWRGcm9tIHN0UmVmOmluc3RhbmNlSUQ9InhtcC5paWQ6M0U2MTczNEREMEQ1MTFFQTg4QUFFMTMxRDExMjdDNTYiIHN0UmVmOmRvY3VtZW50SUQ9InhtcC5kaWQ6M0U2MTczNEVEMEQ1MTFFQTg4QUFFMTMxRDExMjdDNTYiLz4gPC9yZGY6RGVzY3JpcHRpb24+IDwvcmRmOlJERj4gPC94OnhtcG1ldGE+IDw/eHBhY2tldCBlbmQ9InIiPz7GkPa7AAAABlBMVEWer7EAAADVsu9EAAAAAnRSTlP/AOW3MEoAAAAaSURBVAjXY3jHcIyhiYGB4R8QMgBZxxjeAQBAJAZpFFUMegAAAABJRU5ErkJggg=='
                : 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAcAAAAKAQMAAABCAtZOAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAyNpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDYuMC1jMDAyIDc5LjE2NDQ2MCwgMjAyMC8wNS8xMi0xNjowNDoxNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RSZWY9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZVJlZiMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIDIwMjAgTWFjaW50b3NoIiB4bXBNTTpJbnN0YW5jZUlEPSJ4bXAuaWlkOjU5MEIxNkZDRDBENzExRUE4OEFBRTEzMUQxMTI3QzU2IiB4bXBNTTpEb2N1bWVudElEPSJ4bXAuZGlkOjU5MEIxNkZERDBENzExRUE4OEFBRTEzMUQxMTI3QzU2Ij4gPHhtcE1NOkRlcml2ZWRGcm9tIHN0UmVmOmluc3RhbmNlSUQ9InhtcC5paWQ6NTkwQjE2RkFEMEQ3MTFFQTg4QUFFMTMxRDExMjdDNTYiIHN0UmVmOmRvY3VtZW50SUQ9InhtcC5kaWQ6NTkwQjE2RkJEMEQ3MTFFQTg4QUFFMTMxRDExMjdDNTYiLz4gPC9yZGY6RGVzY3JpcHRpb24+IDwvcmRmOlJERj4gPC94OnhtcG1ldGE+IDw/eHBhY2tldCBlbmQ9InIiPz5OiLTZAAAABlBMVEVIbnEAAACz7T8bAAAAAnRSTlP/AOW3MEoAAAAaSURBVAjXY3jHcIyhiYGB4R8QMgBZxxjeAQBAJAZpFFUMegAAAABJRU5ErkJggg=='
            }) no-repeat 0% 49%`,
            bottom: 0,
            position: 'absolute',
            right: 0,
            top: 0,
            WebkitAppearance: 'none',
            width: '1em',
          },
          '&::-webkit-outer-spin-button': {
            WebkitAppearance: 'none',
          },
          position: 'relative',
        },
      }),
    },
    MuiAccordionSummary: {
      styleOverrides: {
        content: {
          '&.Mui-expanded': {
            margin: defaultTheme.spacing(1.5, 0),
          },
          margin: defaultTheme.spacing(1.5, 0),
        },
        expanded: {},
        root: {
          '&.Mui-expanded': {
            minHeight: 0,
          },
          minHeight: 0,
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        position: 'static',
        color: 'default',
      },
      styleOverrides: {
        root: {backgroundImage: 'unset'},
        colorDefault: {
          backgroundColor: palette.background.paper,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ownerState, theme}) => {
          const getColor = theme.palette.mode === 'light' ? darken : lighten;
          const getBackgroundColor =
            theme.palette.mode === 'light' ? lighten : darken;
          const color = ownerState?.color || ownerState?.severity;
          return {
            // Standard variant
            ...(color &&
              ownerState.variant === 'standard' && {
                color: getColor(theme.palette[color].main, 0.6),
                backgroundColor: getBackgroundColor(
                  theme.palette[color].main,
                  0.9,
                ),
              }),
            // Outlined variant
            ...(color &&
              ownerState.variant === 'outlined' && {
                color: getColor(theme.palette[color].main, 0.6),
                border: `1px solid ${theme.palette[color].main}`,
              }),
          };
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(palette.grey[900], 0.65),
        },
        invisible: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            boxShadow: 'none',
          },
        },
        outlined: {
          '&.Mui-disabled': {
            backgroundColor: palette.action.disabledBackground,
          },
        },
        outlinedPrimary: {
          '&:hover': {
            backgroundColor: 'inherit',
          },
          borderColor,
        },
        outlinedSecondary: {
          '&:hover': {
            backgroundColor: 'inherit',
          },
          borderColor,
        },
        sizeLarge: {
          fontSize: pxToRem(16),
        },
        sizeSmall: {
          fontSize: pxToRem(13),
          minWidth: 40,
        },
        textPrimary: {
          '&:hover': {
            backgroundColor: alpha(palette.primary.main, 0.12),
          },
        },
        textSecondary: {
          '&:hover': {
            backgroundColor: alpha(palette.secondary.main, 0.12),
          },
        },
      },

      variants: [
        // Mini Variant
        {
          props: {size: 'mini'},
          style: {
            padding: defaultTheme.spacing(0.5, 1),
            fontSize: defaultTheme.typography.pxToRem(12),
            lineHeight: round(16 / 12),
            minHeight: 24,
            height: 24,
          },
        },
        // Mimic v4 "Default" Button (grey button colors)
        // @see https://mui.com/material-ui/migration/v5-component-changes/#%E2%9C%85-remove-default-color-prop
        // We use "color=grey" as a default prop for the button
        {
          props: {variant: 'contained', color: 'grey'},
          style: {
            backgroundColor: palette.grey[100],
            color: palette.getContrastText(palette.grey[300]),
            '&:hover': {
              color: palette.getContrastText(palette.grey[100]),
              backgroundColor:
                palette.mode === 'light'
                  ? palette.grey[200]
                  : palette.grey[300],
            },
          },
        },
        {
          props: {variant: 'outlined', color: 'grey'},
          style: {
            color: palette.text.primary,
            borderColor:
              palette.mode === 'light'
                ? 'rgba(0, 0, 0, 0.23)'
                : 'rgba(255, 255, 255, 0.23)',
            '&.Mui-disabled': {
              backgroundColor: 'transparent',
              border: `1px solid ${palette.action.disabledBackground}`,
            },
            '&:hover': {
              borderColor:
                palette.mode === 'light'
                  ? 'rgba(0, 0, 0, 0.23)'
                  : palette.text.primary,
              backgroundColor: alpha(
                palette.text.primary,
                palette.action.hoverOpacity,
              ),
            },
          },
        },
        {
          props: {variant: 'text', color: 'grey'},
          style: {
            color: palette.text.primary,
            '&:hover': {
              backgroundColor: alpha(
                palette.text.primary,
                palette.mode === 'light' ? palette.action.hoverOpacity : 0.1,
              ),
            },
          },
        },
      ],
      defaultProps: {
        disableElevation: true,
        color: 'grey',
      },
    },
    MuiCheckbox: {
      defaultProps: {
        indeterminateIcon: <IndeterminateIcon />,
        checkedIcon: <CheckedIcon />,
      },
      styleOverrides: {
        root: ({theme}) => ({
          width: 36,
          height: 36,
          // Input
          '& input': {
            width: 36,
            height: 36,
          },
          // Box
          '&::after': {
            content: '""',
            width: 20,
            height: 20,
            display: 'block',
            border: `1px solid ${theme.palette.grey[500]}`,
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate3d(-50%, -50%, 0)',
            borderRadius: theme.shape.borderRadius,
          },
          // Unchecked (don't show icon)
          '&:not(.Mui-checked):not(.MuiCheckbox-indeterminate) .MuiSvgIcon-root':
            {
              visibility: 'hidden',
            },
          // Disabled
          '&.Mui-disabled': {
            '&.Mui-checked .MuiSvgIcon-root': {
              display: 'inherit',
            },
            '&::after': {
              backgroundColor: theme.palette.action.disabledBackground,
            },
          },
        }),
      },
    },
    MuiChip: {
      variants: [
        {
          props: {shape: 'square'},
          style: {
            borderRadius: defaultTheme.shape.borderRadius,
          },
        },
        {
          props: {size: 'xsmall'},
          style: {
            height: defaultTheme.spacing(2.25),
            '& .MuiChip-label': {
              paddingLeft: defaultTheme.spacing(0.75),
              paddingRight: defaultTheme.spacing(0.75),
              fontSize: defaultTheme.typography.pxToRem(12),
            },
          },
        },
      ],
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-cell': {
            borderBottomColor: borderColor,
            textOverflow: 'ellipsis',
            whiteSpace: 'pre', // don't collapse double spaces
          },
          '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-colCell:focus-within, & .MuiDataGrid-columnHeader:focus-within':
            {
              outline: 0,
            },
          '& .MuiDataGrid-columnsContainer': {
            borderBottomColor: borderColor,
          },
          '& .MuiDataGrid-columnSeparator--resizable': {
            color: borderColor,
          },
          '& .MuiDataGrid-row.focusedNotSelected': {
            backgroundColor: palette.action.disabledBackground,
          },
          borderColor,
          borderRadius: 0,
          borderWidth: '1px 0 0',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paperWidthXs: {
          '&.MuiDialog-paperScrollBody': {
            [defaultTheme.breakpoints.down(
              Math.max(defaultTheme.breakpoints.values.xs, 535) + 32 * 2,
            )]: {
              maxWidth: 'calc(100% - 64px)',
            },
          },
          maxWidth: Math.max(defaultTheme.breakpoints.values.xs, 535),
        },
      },
    },
    MuiLink: {
      defaultProps: {
        color: 'textPrimary',
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: darkmode
              ? palette.primary.dark
              : palette.primary.lighter,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: darkmode
            ? palette.grey[800]
            : palette.background.paper,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected, &.Mui-selected:hover, &:hover': {
            backgroundColor: alpha(palette.primary.main, 0.24),
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor,
        },
        root: {
          '&:focus .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.text.primary,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: ({theme}) => ({
          width: 38,
          height: 38,
          '&::before': {
            content: "''",
            width: 20,
            height: 20,
            borderRadius: '50%',
            border: `1px solid ${theme.palette.grey[500]}`,
            position: 'absolute',
          },
          '& .MuiSvgIcon-root': {
            display: 'none', // Hide Both Icons
            // 1st SVG is the outer (border) icon
            //'&:first-of-type': {},
            // 2nd SVG is the inner (checked) icon
            '&:nth-of-type(2)': {
              left: '50%',
              top: '50%',
              transform: 'translate3d(-50%, -50%, 0)',
              width: 29,
              height: 29,
            },
          },
          '&.Mui-checked': {
            '& .MuiSvgIcon-root:nth-of-type(2)': {
              display: 'inherit',
            },
          },

          '&.Mui-disabled': {
            '&::before': {
              backgroundColor: theme.palette.action.disabledBackground,
            },
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: palette.grey[900],
          borderRadius: 10,
          color: palette.getContrastText(palette.grey[900]),
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        colorPrimary: {
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: palette.primary[palette.mode],
            opacity: 1,
          },
        },
        root: {
          alignItems: 'center',
        },
        switchBase: {
          color: darkmode ? palette.grey[500] : palette.grey[400],
        },
        track: {
          backgroundColor: darkmode ? palette.grey[700] : palette.grey[200],
          borderRadius: 3.5,
          height: defaultTheme.spacing(1),
          opacity: 1,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          paddingBottom: 0,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: borderColor,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderColor,
        },
        sizeSmall: {
          padding: defaultTheme.spacing(0.625, 1.25), // @see https://github.com/mui-org/material-ui/issues/20919 - consolidating toggle button with default button sizes
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        dense: {
          minHeight: 40,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        arrow: {
          color: darkmode ? palette.grey[300] : palette.grey[900],
        },
        tooltip: {
          backgroundColor: darkmode ? palette.grey[300] : palette.grey[900],
          color: palette.getContrastText(
            darkmode ? palette.grey[300] : palette.grey[900],
          ),
          fontSize: pxToRem(13),
          lineHeight: `${round(16 / 12)}em`,
        },
      },
    },
  };
};
